import React, { useEffect, useState } from 'react';

import { useAppSelector } from 'store/hooks';

/** Thời gian (ms) banner "Đã kết nối lại" tự ẩn sau khi mạng khôi phục */
const RECONNECTED_DISPLAY_MS = 2000;

const NetworkStatusModal: React.FC = () => {
  const { isOnline, wasOffline } = useAppSelector((state) => state.system);

  /**
   * `showReconnected` = true → hiển thị banner "Đã kết nối lại"
   * trong RECONNECTED_DISPLAY_MS ms rồi tự ẩn.
   */
  const [showReconnected, setShowReconnected] = useState<boolean>(false);

  useEffect(() => {
    if (isOnline && wasOffline) {
      setShowReconnected(true);
      const timerId = setTimeout(() => {
        setShowReconnected(false);
      }, RECONNECTED_DISPLAY_MS);

      return () => clearTimeout(timerId);
    }
    return undefined;
  }, [isOnline, wasOffline]);

  /* Không render gì khi mạng ổn định và chưa từng mất */
  if (isOnline && !showReconnected) return null;

  return (
    <div
      className="t-networkStatusModal"
      role="alertdialog"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="t-networkStatusModal_overlay" />

      <div
        className={`t-networkStatusModal_card ${!isOnline ? 't-networkStatusModal_card--offline' : 't-networkStatusModal_card--online'
          }`}
      >
        {/* Icon */}
        <div className="t-networkStatusModal_icon">
          {!isOnline ? (
            /* Wifi-off SVG */
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="1" y1="1" x2="23" y2="23" />
              <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
              <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
              <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
              <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <line x1="12" y1="20" x2="12.01" y2="20" />
            </svg>
          ) : (
            /* Checkmark SVG */
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>

        {/* Content */}
        <div className="t-networkStatusModal_content">
          <p className="t-networkStatusModal_title">
            {!isOnline ? 'Mất kết nối internet' : 'Đã kết nối lại internet'}
          </p>
          <p className="t-networkStatusModal_desc">
            {!isOnline
              ? 'Vui lòng kiểm tra kết nối internet của bạn.'
              : 'Kết nối internet của bạn đã được khôi phục.'}
          </p>
        </div>

        {/* Progress bar (only for "reconnected" state) */}
        {isOnline && showReconnected && (
          <div
            className="t-networkStatusModal_progress"
            style={{ animationDuration: `${RECONNECTED_DISPLAY_MS}ms` }}
          />
        )}
      </div>
    </div>
  );
};

export default NetworkStatusModal;
